import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';
import {
  ChatGroup,
  OnlineUser,
  TypingUser,
  ActiveEditor,
  SetupChangedEvent
} from '@/types/chat';

// Socket.io uses its own /socket.io path, which the Vite dev proxy does not forward, so
// it connects straight to the API port. Derive the host from the page instead of
// hardcoding localhost, so this works whether you loaded the app via localhost, a LAN IP
// or Tailscale. (Socket auth is token-based, so being cross-origin here is fine.)
const DEV_SOCKET_URL = (
  import.meta.env.VITE_API_URL ||
  `${window.location.protocol}//${window.location.hostname}:5001`
).replace(/^http/, 'ws');

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  groups: ChatGroup[];
  onlineUsers: OnlineUser[];
  typingUsers: TypingUser[];

  // Actions
  // NOTE: message state lives in useChatLogic (the rendered source of truth).
  // SocketContext intentionally does NOT keep a second messages store.
  editMessage: (messageId: string, content: string) => Promise<boolean>;
  deleteMessage: (messageId: string) => Promise<boolean>;
  addReaction: (messageId: string, reactionType: string) => Promise<void>;
  markAsRead: (groupId: string, messageIds?: string[]) => void;
  setTyping: (groupId: string, isTyping: boolean) => void;
  joinGroup: (groupId: string) => void;
  leaveGroup: (groupId: string) => void;

  // Group management
  refreshGroups: () => void;

  // Connection management
  reconnectSocket: () => void;

  // Setup collaboration
  joinSetup: (setupId: string) => void;
  leaveSetup: (setupId: string) => void;
  notifySetupUpdate: (setupId: string, changes: any, version: number) => void;
  onSetupChanged: (callback: (event: SetupChangedEvent) => void) => () => void;
  onSetupEditorJoined: (callback: (event: { setupId: string; userId: string; userName: string; userPosition?: string; activeEditors: ActiveEditor[] }) => void) => () => void;
  onSetupEditorLeft: (callback: (event: { setupId: string; userId: string; userName: string; activeEditors: ActiveEditor[] }) => void) => () => void;
  onSetupActiveEditors: (callback: (event: { setupId: string; activeEditors: ActiveEditor[] }) => void) => () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [groups, setGroups] = useState<ChatGroup[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);

  // Keep a ref to the current socket to avoid stale closures in timers
  const socketRef = useRef<Socket | null>(null);

  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const typingTimeoutRef = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const visibilityTimeoutRef = useRef<NodeJS.Timeout>();

  // Socket token refresh management
  const tokenRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingTokenRef = useRef<boolean>(false);
  const socketTokenMetaRef = useRef<{ token: string; exp?: number } | null>(null);

  const clearTokenRefreshTimer = () => {
    if (tokenRefreshTimeoutRef.current) {
      clearTimeout(tokenRefreshTimeoutRef.current);
      tokenRefreshTimeoutRef.current = null;
    }
  };

  const decodeJwtExp = (token: string): number | undefined => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      return typeof payload.exp === 'number' ? payload.exp : undefined;
    } catch {
      return undefined;
    }
  };

  const scheduleSocketTokenRefresh = (token?: string) => {
    clearTokenRefreshTimer();
    let delayMs = 22 * 60 * 60 * 1000; // default 22 hours (for 24h tokens)
    if (token) {
      const exp = decodeJwtExp(token);
      if (exp) {
        const msUntilExp = exp * 1000 - Date.now();
        // NEVER TIMEOUT: Refresh 2 hours before expiry, minimum 30s to avoid tight loops
        delayMs = Math.max(30_000, msUntilExp - 2 * 60 * 60 * 1000);
        socketTokenMetaRef.current = { token, exp };
      } else {
        socketTokenMetaRef.current = { token };
      }
    }
    tokenRefreshTimeoutRef.current = setTimeout(() => {
      refreshSocketToken('scheduled');
    }, delayMs);
  };

  const fetchSocketToken = async (): Promise<string | null> => {
    try {
      const response = await fetch('/api/auth/socket-token', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.status === 401) {
        // Attempt silent refresh if access token is missing/expired
        try {
          const authService = (await import('@/services/authService')).default;
          const refreshed = await authService.silentRefresh();
          if (!refreshed) return null;
          // Retry once after successful refresh
          const retry = await fetch('/api/auth/socket-token', {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          });
          if (!retry.ok) return null;
          const retryData = await retry.json();
          return retryData.token as string;
        } catch {
          return null;
        }
      }

      if (!response.ok) return null;
      const data = await response.json();
      return data.token as string;
    } catch {
      return null;
    }
  };

  const refreshSocketToken = async (reason: 'scheduled' | 'connect_error' | 'manual' = 'manual') => {
    if (isRefreshingTokenRef.current) return;
    isRefreshingTokenRef.current = true;
    try {
      const newToken = await fetchSocketToken();
      const currentSocket = socketRef.current;
      if (!newToken || !currentSocket) return;

      // Update auth token used for handshake
      (currentSocket as any).auth = { token: newToken };
      scheduleSocketTokenRefresh(newToken);

      // Force a silent reconnect to apply new auth token
      const wasConnected = currentSocket.connected;
      if (wasConnected) {
        // Disconnect without triggering user-visible errors, then reconnect
        currentSocket.disconnect();
        setTimeout(() => {
          const s = socketRef.current;
          if (s && !s.connected) {
            s.connect();
          }
        }, 200);
      } else {
        currentSocket.connect();
      }
    } finally {
      isRefreshingTokenRef.current = false;
    }
  };

  // Handle mobile app lifecycle - reconnect when app becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && socket && !socket.connected) {
        // Clear any existing timeout
        if (visibilityTimeoutRef.current) {
          clearTimeout(visibilityTimeoutRef.current);
        }

        // Reconnect after a short delay to ensure app is fully active
        visibilityTimeoutRef.current = setTimeout(() => {
          if (socket && !socket.connected) {
            socket.connect();
          }
        }, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }
    };
  }, [socket]);

  // Initialize socket connection
  useEffect(() => {
    const initializeSocket = async () => {
      // Add error boundary for socket initialization
      try {
        if (!user) {
          if (socket) {
            socket.disconnect();
            setSocket(null);
            setIsConnected(false);
          }
          socketRef.current = null;
          // Clear any scheduled token refresh when user logs out or is missing
          clearTokenRefreshTimer();
          return;
        }

        // For httpOnly cookie authentication, we need to get the token from the server
        // Since we can't access httpOnly cookies from JavaScript, we'll make a request to get a socket token
        let socketToken;
        try {
          const response = await fetch('/api/auth/socket-token', {
            method: 'GET',
            credentials: 'include', // Include httpOnly cookies
            headers: {
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            socketToken = data.token;
          } else {
            console.warn('Failed to get socket authentication token');
            return;
          }
        } catch (error) {
          return;
        }

        if (!socketToken) {
          console.warn('No socket authentication token received');
          return;
        }

    const newSocket = io(import.meta.env.PROD
      ? 'https://cfa-eval-7eb74e14c3a4.herokuapp.com'
      : DEV_SOCKET_URL, {
      auth: { token: socketToken },
      // NEVER TIMEOUT: Aggressive connection settings to prevent disconnections
      timeout: 60000, // 60 seconds connection timeout
      reconnection: true,
      reconnectionDelay: 1000, // Start with 1 second delay
      reconnectionDelayMax: 10000, // Max 10 seconds delay
      reconnectionAttempts: Infinity, // Never give up reconnecting
      forceNew: false, // Reuse existing connection if possible
      // Upgrade transport settings
      upgrade: true,
      rememberUpgrade: true,
      // Additional stability settings
      randomizationFactor: 0.5, // Add randomization to reconnection delay
      // Keep connection alive
      autoConnect: true,
      // Force websocket transport for better performance (prefer websocket, fallback to polling)
      transports: ['websocket', 'polling']
    });

    // Store in ref for use in timers/callbacks
    socketRef.current = newSocket;

    // Schedule auto-refresh ~5m before expiry (or 55m default)
    scheduleSocketTokenRefresh(socketToken);

    // Connection events
    newSocket.on('connect', () => {
      const wasDisconnected = !isConnected;
      setIsConnected(true);

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // Log reconnection success silently (no user notification)
      if (wasDisconnected) {
        // Reconnection successful, no action needed
      }

      // Request online users when connected
      newSocket.emit('get_online_users');
    });

    // NEVER TIMEOUT: Handle server heartbeat to keep connection alive
    newSocket.on('heartbeat', (data) => {
      // Respond to server heartbeat immediately
      newSocket.emit('heartbeat_response', { timestamp: Date.now(), serverTimestamp: data.timestamp });
      console.debug('Heartbeat received and responded');
    });

    newSocket.on('disconnect', (reason) => {
      setIsConnected(false);

      // Don't auto-reconnect if disconnected intentionally
      if (reason === 'io client disconnect') {
        return;
      }

      // Attempt to reconnect after 3 seconds for other disconnect reasons
      reconnectTimeoutRef.current = setTimeout(() => {
        if (newSocket && !newSocket.connected) {
          newSocket.connect();
        }
      }, 3000);
    });

    newSocket.on('connect_error', (error: any) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);

      // If the error indicates the socket auth token expired, refresh and reconnect
      const name = error?.name || error?.data?.name;
      const message: string = error?.message || '';
      if (name === 'TokenExpiredError' || /invalid token|authentication error/i.test(message)) {
        refreshSocketToken('connect_error');
        return;
      }
      // Otherwise let built-in reconnection logic handle it silently
    });

    // Chat events
    newSocket.on('user_groups', (userGroups: ChatGroup[]) => {
      setGroups(userGroups);
    });

    // Message delivery + read receipts are handled in useChatLogic against the
    // rendered message array — no duplicate store here.

    // Online users events
    newSocket.on('online_users', (users: OnlineUser[]) => {
      setOnlineUsers(users);
    });

    newSocket.on('user_online', ({ userId }: { userId: string }) => {
      setOnlineUsers(prev => {
        if (prev.some(u => u.userId === userId)) return prev;
        return [...prev, { userId, connectedAt: new Date().toISOString() }];
      });
    });

    newSocket.on('user_offline', ({ userId }: { userId: string }) => {
      setOnlineUsers(prev => prev.filter(u => u.userId !== userId));
    });

    // Group management events
    newSocket.on('group_created', ({ group, createdBy, reopened }: { group: ChatGroup, createdBy: string, reopened?: boolean }) => {
      setGroups(prev => {
        // Check if group already exists to prevent duplicates
        const exists = prev.some(existingGroup => existingGroup._id === group._id);
        if (exists) {
          return prev;
        }
        return [...prev, group];
      });

      // A "cleared" conversation resurfacing on a new message already gets the
      // message toast — don't stack a second "started a conversation" one.
      if (reopened) return;

      // Show notification if user was added to the group (but didn't create it).
      // DMs have no name, so use a message-oriented toast instead of a group one.
      if (user && group.createdBy && group.createdBy._id !== user._id) {
        if (group.type === 'direct') {
          showNotification('info', 'New message', `${createdBy} started a conversation with you`,
            { url: `/chat?group=${group._id}` });
        } else {
          showNotification('info', 'Added to New Group',
            `${createdBy} added you to "${group.name}"`, { url: `/chat?group=${group._id}` });
        }
      }
    });

    newSocket.on('group_updated', ({ group, updatedBy }) => {
      setGroups(prev => prev.map(g => g._id === group._id ? group : g));
      if (user && updatedBy !== user.name) {
        showNotification('info', 'Group updated', `${updatedBy} updated "${group.name}"`);
      }
    });

    newSocket.on('group_deleted', ({ groupId, groupName, deletedBy }) => {
      setGroups(prev => prev.filter(g => g._id !== groupId));
      if (user && deletedBy !== user.name) {
        showNotification('info', 'Group deleted', `${deletedBy} deleted "${groupName}"`);
      }
    });

    newSocket.on('added_to_group', ({ group, addedBy }) => {
      setGroups(prev => {
        // Check if group already exists to prevent duplicates
        const exists = prev.some(existingGroup => existingGroup._id === group._id);
        if (exists) {
          return prev;
        }
        return [...prev, group];
      });
      showNotification('info', 'Added to group', `${addedBy} added you to "${group.name}"`);
    });

    newSocket.on('removed_from_group', ({ groupId, groupName, removedBy }) => {
      setGroups(prev => prev.filter(g => g._id !== groupId));
      showNotification('info', 'Removed from group', `${removedBy} removed you from "${groupName}"`);
    });



    newSocket.on('group_member_permissions_updated', ({ groupId, memberId, canAddMembers, updatedBy }: {
      groupId: string,
      memberId: string,
      canAddMembers: boolean,
      updatedBy: string
    }) => {
      setGroups(prev => prev.map(group => {
        if (group._id === groupId) {
          return {
            ...group,
            members: group.members.map(member =>
              member.user._id === memberId
                ? { ...member, canAddMembers }
                : member
            )
          };
        }
        return group;
      }));

      // Show notification if someone else updated the permissions
      if (user && updatedBy !== user.name) {
        showNotification('info', 'Member Permissions Updated',
          `${updatedBy} updated member permissions`);
      }
    });

    newSocket.on('mention_notification', ({ messageId, groupId, groupName, isDirect, senderName, content, mentionType }: {
      messageId: string,
      groupId: string,
      groupName: string,
      isDirect?: boolean,
      senderName: string,
      content: string,
      mentionType: 'direct' | 'reply'
    }) => {
      // Show notification if:
      // 1. Page is not focused (user is on another tab/window), OR
      // 2. Not on chat page, OR
      // 3. On chat page but page is not focused
      // This ensures users get notified when they're not actively viewing the chat
      const isOnChatPage = window.location.pathname.includes('/chat');
      const isPageFocused = document.hasFocus();
      
      // Always show mention notifications - they're important
      // But skip if user is actively viewing this specific group and page is focused
      let shouldShow = true;
      if (isOnChatPage && isPageFocused) {
        // Try to detect if this group is currently selected
        // Check for active group indicators in the DOM
        const activeGroup = document.querySelector(`[data-group-id="${groupId}"][data-active="true"]`) ||
          document.querySelector(`[data-group-id="${groupId}"].active`) ||
          document.querySelector(`[data-selected-group-id="${groupId}"]`);
        
        // If we can't detect, default to showing the notification (better safe than sorry)
        shouldShow = activeGroup === null;
      }
      
      if (shouldShow) {
        const mentionEmoji = mentionType === 'direct' ? '🔔' : '💬';
        const truncatedContent = content.length > 60 ? content.substring(0, 60) + '...' : content;
        const chatUrl = `/chat?group=${groupId}${messageId ? `&messageId=${messageId}` : ''}`;
        if (isDirect) {
          // DM: title is the sender, body is just the message.
          showNotification('info', `${mentionEmoji} ${groupName}`, truncatedContent, { url: chatUrl });
        } else {
          showNotification('info', `${mentionEmoji} Mentioned in ${groupName}`,
            `${senderName}: ${truncatedContent}`, { url: chatUrl });
        }
      }
    });

    // Keep the sidebar's last-message preview + ordering fresh without a
    // refetch. new_message is a group-room broadcast, so this fires for every
    // conversation the user belongs to, not just the one on screen.
    newSocket.on('new_message', (message: {
      _id: string,
      group: string | { _id: string },
      sender?: { _id: string, name: string },
      content?: string,
      messageType?: string,
      createdAt: string
    }) => {
      const messageGroupId = typeof message.group === 'string' ? message.group : message.group?._id;
      if (!messageGroupId) return;
      setGroups(prev => prev.map(group =>
        group._id === messageGroupId
          ? {
              ...group,
              lastActivity: message.createdAt,
              lastMessage: {
                messageId: message._id || null,
                content: message.content || '',
                messageType: (message.messageType || 'text') as NonNullable<ChatGroup['lastMessage']>['messageType'],
                senderId: message.sender?._id || null,
                senderName: message.sender?.name || '',
                createdAt: message.createdAt
              }
            }
          : group
      ));
    });

    // Keep the preview honest when the newest message changes under it. Both
    // events are group-room broadcasts, so they reach every member. On delete
    // we can't know the previous message from here — show a neutral line and
    // let the next groups fetch restore the real one.
    newSocket.on('message_edited', ({ messageId, content }: { messageId: string, content: string }) => {
      setGroups(prev => prev.map(group => {
        if (!group.lastMessage || group.lastMessage.messageId !== messageId) return group;
        return { ...group, lastMessage: { ...group.lastMessage, content } };
      }));
    });

    newSocket.on('message_deleted', ({ messageId, groupId, groupLastMessage }: {
      messageId: string,
      groupId?: string,
      groupLastMessage?: ChatGroup['lastMessage']
    }) => {
      setGroups(prev => prev.map(group => {
        if (!group.lastMessage || group.lastMessage.messageId !== messageId) return group;
        // The server sends the group's new latest message alongside the delete,
        // so the preview shows the real previous message. Fall back to a
        // neutral line if the payload predates this field.
        if (groupId && group._id === groupId) {
          return { ...group, lastMessage: groupLastMessage || null };
        }
        return { ...group, lastMessage: { ...group.lastMessage, content: 'Message deleted', messageType: 'system' as const } };
      }));
    });

    // Handle regular chat notifications (for all group members)
    newSocket.on('chat_notification', ({ messageId, groupId, groupName, isDirect, senderName, content, type }: {
      messageId: string,
      groupId: string,
      groupName: string,
      isDirect?: boolean,
      senderName: string,
      content: string,
      type: 'new_message' | 'file_upload' | 'poll'
    }) => {
      // Show notification if:
      // 1. Page is not focused (user is on another tab/window), OR
      // 2. Not on chat page, OR
      // 3. On chat page but not viewing this specific group
      const isOnChatPage = window.location.pathname.includes('/chat');
      const isPageFocused = document.hasFocus();
      
      let shouldShow = true;
      if (isOnChatPage && isPageFocused) {
        // Try to detect if this group is currently selected
        const activeGroup = document.querySelector(`[data-group-id="${groupId}"][data-active="true"]`) ||
          document.querySelector(`[data-group-id="${groupId}"].active`) ||
          document.querySelector(`[data-selected-group-id="${groupId}"]`);
        
        // If we can't detect, default to showing the notification
        shouldShow = activeGroup === null;
      }
      
      if (shouldShow) {
        const fileEmoji = type === 'file_upload' ? '📎' : type === 'poll' ? '📊' : '💬';
        const truncatedContent = content.length > 60 ? content.substring(0, 60) + '...' : content;
        const chatUrl = `/chat?group=${groupId}${messageId ? `&messageId=${messageId}` : ''}`;
        // DM: title is the sender, body is just the message (no redundant prefix).
        showNotification('info', `${fileEmoji} ${groupName}`,
          isDirect ? truncatedContent : `${senderName}: ${truncatedContent}`, { url: chatUrl });
      }
    });

    // Note: message reaction / edit / delete events are handled in useChatLogic
    // against the rendered message array to avoid a divergent second store.

    newSocket.on('user_typing', (data: TypingUser) => {
      setTypingUsers(prev => {
        const filtered = prev.filter(u => u.userId !== data.userId || u.groupId !== data.groupId);
        if (data.isTyping) {
          return [...filtered, data];
        }
        return filtered;
      });

      // Clear typing indicator after 3 seconds
      const key = `${data.userId}-${data.groupId}`;
      if (typingTimeoutRef.current[key]) {
        clearTimeout(typingTimeoutRef.current[key]);
      }

      if (data.isTyping) {
        typingTimeoutRef.current[key] = setTimeout(() => {
          setTypingUsers(prev => prev.filter(u => u.userId !== data.userId || u.groupId !== data.groupId));
        }, 3000);
      }
    });



    newSocket.on('error', ({ message }) => {
      console.error('Socket error:', message);
      // Handle socket errors silently - no user notifications
    });

    setSocket(newSocket);

        return () => {
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          if (visibilityTimeoutRef.current) {
            clearTimeout(visibilityTimeoutRef.current);
          }
          clearTokenRefreshTimer();
          Object.values(typingTimeoutRef.current).forEach(timeout => clearTimeout(timeout));
          newSocket.disconnect();
        };
      } catch (error) {
        setIsConnected(false);
        // Handle initialization errors silently - no user notifications
      }
    };

    // Call the async function
    initializeSocket();
  }, [user, showNotification]);

  // Actions
  // (Sending is HTTP-only — see useChatLogic.sendMessageWithOfflineSupport.)
  //
  // 2026-09-26 stall: every action below used to be a fresh function on every
  // render, and the provider value a fresh object. ChatWindow's mark-as-read
  // effect listed markAsRead as a dependency, so each store-wide user_online /
  // user_offline, every new_message in any group and every typing blip
  // re-sent mark_as_read for the open chat — 5,898 chatgroups writes in seven
  // minutes on Calhoun's Whole Store group. The actions are now memoized on
  // what they actually read (the socket and whether it is connected), and the
  // value only changes when something a consumer can see changes.
  const editMessage = useCallback(async (messageId: string, content: string): Promise<boolean> => {
    try {
      const response = await fetch(`/api/chat/messages/${messageId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Validator failures carry the specific reason in errors[0].message
        // (errorData.message is just "Validation failed").
        showNotification('error', 'Error', errorData.errors?.[0]?.message || errorData.message || 'Failed to edit message');
        return false;
      }

      showNotification('success', 'Success', 'Message edited successfully');
      return true;
    } catch (error) {
      showNotification('error', 'Error', 'Failed to edit message');
      return false;
    }
  }, [showNotification]);

  const deleteMessage = useCallback(async (messageId: string): Promise<boolean> => {
    try {
      const response = await fetch(`/api/chat/messages/${messageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        showNotification('error', 'Error', errorData.message || 'Failed to delete message');
        return false;
      }

      showNotification('success', 'Success', 'Message deleted successfully');
      return true;
    } catch (error) {
      showNotification('error', 'Error', 'Failed to delete message');
      return false;
    }
  }, [showNotification]);

  const addReaction = useCallback(async (messageId: string, reactionType: string): Promise<void> => {
    try {
      const response = await fetch(`/api/chat/messages/${messageId}/reactions`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reactionType })
      });

      if (!response.ok) {
        throw new Error('Failed to add reaction');
      }
    } catch (error) {
    }
  }, []);

  // Identity changes only when the socket connects, disconnects or is
  // replaced. That is deliberate: a mark-as-read made while disconnected is
  // dropped, and the new identity on reconnect is what makes the open chat
  // mark itself read again.
  const markAsRead = useCallback((groupId: string, messageIds?: string[]) => {
    if (socket && isConnected) {
      socket.emit('mark_as_read', { groupId, messageIds });
    }
  }, [socket, isConnected]);

  const setTyping = useCallback((groupId: string, isTyping: boolean) => {
    if (socket && isConnected) {
      socket.emit('typing', { groupId, isTyping });
    }
  }, [socket, isConnected]);

  const joinGroup = useCallback((groupId: string) => {
    if (socket && isConnected) {
      socket.emit('join_group', { groupId });
    }
  }, [socket, isConnected]);

  const leaveGroup = useCallback((groupId: string) => {
    if (socket && isConnected) {
      socket.emit('leave_group', { groupId });
    }
  }, [socket, isConnected]);

  const refreshGroups = useCallback(() => {
    if (socket && isConnected) {
      socket.emit('get_user_groups');
    } else {
      // Socket not connected, skip
    }
  }, [socket, isConnected]);

  const reconnectSocket = useCallback(() => {
    if (socket && !socket.connected) {
      socket.connect();
    }
  }, [socket]);

  // ============= SETUP COLLABORATION METHODS =============

  const joinSetup = useCallback((setupId: string) => {
    if (socket && isConnected) {
      socket.emit('join_setup', { setupId });
    }
  }, [socket, isConnected]);

  const leaveSetup = useCallback((setupId: string) => {
    if (socket && isConnected) {
      socket.emit('leave_setup', { setupId });
    }
  }, [socket, isConnected]);

  const notifySetupUpdate = useCallback((setupId: string, changes: any, version: number) => {
    if (socket && isConnected) {
      socket.emit('setup_updated', { setupId, changes, version });
    }
  }, [socket, isConnected]);

  const onSetupChanged = useCallback((callback: (event: SetupChangedEvent) => void) => {
    if (socket) {
      socket.on('setup_changed', callback);
      return () => {
        socket.off('setup_changed', callback);
      };
    }
    return () => {};
  }, [socket]);

  const onSetupEditorJoined = useCallback((callback: (event: any) => void) => {
    if (socket) {
      socket.on('setup_editor_joined', callback);
      return () => {
        socket.off('setup_editor_joined', callback);
      };
    }
    return () => {};
  }, [socket]);

  const onSetupEditorLeft = useCallback((callback: (event: any) => void) => {
    if (socket) {
      socket.on('setup_editor_left', callback);
      return () => {
        socket.off('setup_editor_left', callback);
      };
    }
    return () => {};
  }, [socket]);

  const onSetupActiveEditors = useCallback((callback: (event: any) => void) => {
    if (socket) {
      socket.on('setup_active_editors', callback);
      return () => {
        socket.off('setup_active_editors', callback);
      };
    }
    return () => {};
  }, [socket]);

  const value = useMemo<SocketContextType>(() => ({
    socket,
    isConnected,
    groups,
    onlineUsers,
    typingUsers,
    editMessage,
    deleteMessage,
    addReaction,
    markAsRead,
    setTyping,
    joinGroup,
    leaveGroup,
    refreshGroups,
    reconnectSocket,
    joinSetup,
    leaveSetup,
    notifySetupUpdate,
    onSetupChanged,
    onSetupEditorJoined,
    onSetupEditorLeft,
    onSetupActiveEditors
  }), [
    socket,
    isConnected,
    groups,
    onlineUsers,
    typingUsers,
    editMessage,
    deleteMessage,
    addReaction,
    markAsRead,
    setTyping,
    joinGroup,
    leaveGroup,
    refreshGroups,
    reconnectSocket,
    joinSetup,
    leaveSetup,
    notifySetupUpdate,
    onSetupChanged,
    onSetupEditorJoined,
    onSetupEditorLeft,
    onSetupActiveEditors
  ]);

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
